import React, { useState, useMemo } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock3,
  Eye,
  FileText,
  LoaderCircle,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Wrench,
  X,
} from 'lucide-react';
import { usePendingRegistrations } from '../hooks/usePendingRegistrations';
import { PendingRegistration, ServiceProviderProfile, serviceProviderService } from '../services/serviceProviderService';
import { formatDateTimeString } from '../lib/utils';

const filterControlClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

export const PendingRegistrations: React.FC = () => {
  const {
    pendingRegistrations,
    loading,
    error,
    pagination,
    serviceProviderTypes,
    serviceProviderTypeFilter,
    setCurrentPage,
    setItemsPerPage,
    setServiceProviderTypeFilter,
    refresh
  } = usePendingRegistrations();

  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof PendingRegistration | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [selectedProfile, setSelectedProfile] = useState<PendingRegistration | ServiceProviderProfile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filter and sort pending registrations (client-side filtering for search and location)
  const filteredAndSortedPendingRegistrations = useMemo(() => {
    if (!pendingRegistrations) return [];
    
    let filtered = pendingRegistrations.filter(registration => {
      const matchesSearch = registration.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          registration.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          registration.contactNumber.includes(searchTerm) ||
                          registration.tradeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          registration.email.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [pendingRegistrations, searchTerm, sortConfig]);
  const activeFilterCount = [Boolean(searchTerm.trim()), Boolean(serviceProviderTypeFilter)].filter(Boolean).length;

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, setCurrentPage]);

  const handleSort = (key: keyof PendingRegistration) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey: keyof PendingRegistration) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />
      : <ArrowDown className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />;
  };

  const handleApprove = (registration: PendingRegistration) => {
    setSelectedProfile(registration);
    setActionType('approve');
    setShowConfirmModal(true);
  };

  const handleReject = (registration: PendingRegistration) => {
    setSelectedProfile(registration);
    setActionType('reject');
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    if (!selectedProfile || !actionType) return;

    setActionLoading(true);
    try {
      const status = actionType === 'approve' ? 'activate' : 'reject';
      const reason = actionType === 'approve' ? 'Approved by admin' : 'Rejected by admin';
      
      // Service provider action logging removed for security
      
      await serviceProviderService.updatePendingServiceProviderStatus({
        mobileNumber: selectedProfile.contactNumber,
        status: status,
        reason: reason
      });
      
      // Service provider action success logging removed for security
      
      setActionMessage({
        type: 'success',
        text: `Service provider ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`
      });
      
      // Close confirmation modal
      setShowConfirmModal(false);
      setActionType(null);
      setSelectedProfile(null);
      
      // Refresh the data
      await refresh();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionMessage(null);
      }, 3000);
      
    } catch (error) {
      console.error(`❌ Error ${actionType === 'approve' ? 'approving' : 'rejecting'} service provider:`, error);
      
      setActionMessage({
        type: 'error',
        text: `Failed to ${actionType === 'approve' ? 'approve' : 'reject'} service provider. Please try again.`
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setActionMessage(null);
      }, 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelAction = () => {
    setShowConfirmModal(false);
    setActionType(null);
    setSelectedProfile(null);
  };

  const handleView = async (registration: PendingRegistration) => {
    try {
      setProfileLoading(true);
      setIsModalOpen(true);
      setSelectedProfile(registration);
      
      // Service provider details fetch logging removed for security
      const response = await serviceProviderService.getServiceProviderProfile(registration.contactNumber);
      
      if (response.success) {
        // Service provider profile fetch success logging removed for security
        setSelectedProfile(response.data.serviceProvider);
      } else {
        console.error('❌ Failed to fetch service provider profile:', response.message);
        setSelectedProfile(null);
      }
    } catch (error) {
      console.error('❌ Error fetching service provider profile:', error);
      setSelectedProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
              Registration queue
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Pending Registrations</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Review incomplete provider registrations, verify profiles, and take action from one queue.</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Pending registration summary">
        {/* Total Registrations Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Registrations</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (pendingRegistrations?.length || 0).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">In the current queue</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50"><FileText className="h-5 w-5 text-blue-700" aria-hidden="true" /></span>
          </div>
        </article>

        {/* Pending Verification Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Awaiting review</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (pendingRegistrations?.filter(r => r.verificationStatus === 'pending').length || 0).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">Pending verification</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50"><Clock3 className="h-5 w-5 text-amber-700" aria-hidden="true" /></span>
          </div>
        </article>

        {/* Services Offered Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Services</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (pendingRegistrations?.reduce((sum, r) => sum + r.servicesOffered, 0) || 0).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">Services offered</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50"><Wrench className="h-5 w-5 text-violet-700" aria-hidden="true" /></span>
          </div>
        </article>

        {/* Payment Status Card */}
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Payment pending</p>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {loading ? '…' : (pendingRegistrations?.filter(r => r.paymentStatus === 'pending').length || 0).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-slate-500">Awaiting completion</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50"><CircleAlert className="h-5 w-5 text-red-700" aria-hidden="true" /></span>
          </div>
        </article>
      </section>

      {/* Results Table */}
      <div className="admin-table-shell">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><FileText className="h-[18px] w-[18px]" aria-hidden="true" /></span><div><h2 className="text-base font-semibold text-slate-950">Registration review queue</h2><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {filteredAndSortedPendingRegistrations.length} of {pagination?.totalCount ?? pendingRegistrations.length} registrations</p></div></div>
          <button type="button" onClick={() => setAreFiltersOpen(current => !current)} aria-expanded={areFiltersOpen} aria-controls="pending-registration-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
        </div>
        <div id="pending-registration-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex-1"><label htmlFor="pending-registration-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search registrations</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="pending-registration-search" type="search" placeholder="Name, ID, contact, trade name, or email" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} className={`${filterControlClassName} pl-10`} /></div></div>
            <div className="md:w-60"><label htmlFor="pending-service-type" className="mb-1.5 block text-xs font-semibold text-slate-600">Service type</label><select id="pending-service-type" value={serviceProviderTypeFilter} onChange={(event) => setServiceProviderTypeFilter(event.target.value)} className={filterControlClassName}><option value="">All service types</option>{serviceProviderTypes.filter(type => type.tag && type.name).map(type => <option key={type.tag} value={type.tag}>{type.name}</option>)}</select></div>
            <div className="md:w-36"><label htmlFor="pending-items-per-page" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label><select id="pending-items-per-page" className={filterControlClassName} value={pagination?.limit || 10} onChange={(event) => setItemsPerPage(Number(event.target.value))}><option value={5}>5 per page</option><option value={10}>10 per page</option><option value={25}>25 per page</option><option value={50}>50 per page</option></select></div>
          </div>
          {activeFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => { setSearchTerm(''); setServiceProviderTypeFilter(''); setCurrentPage(1); }} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14">
            <LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-900">Loading pending registrations…</h3>
            <p className="mt-1 text-sm text-slate-500">Fetching the latest registration records.</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center px-6 py-14 text-center" role="alert">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-600"><CircleAlert className="h-5 w-5" aria-hidden="true" /></span>
            <h3 className="text-sm font-semibold text-slate-900">Unable to load registrations</h3>
            <p className="mt-1 max-w-md text-sm text-slate-500">{error}</p>
            <button
              type="button"
              onClick={refresh}
              className="mt-4 inline-flex h-10 items-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              Try again
            </button>
          </div>
        ) : filteredAndSortedPendingRegistrations.length === 0 ? (
          <div className="flex flex-col items-center py-14 text-center">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><FileText className="h-5 w-5" aria-hidden="true" /></span>
            <h3 className="text-sm font-semibold text-slate-900">No pending registrations found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {searchTerm || serviceProviderTypeFilter
                ? 'Try adjusting your search criteria.'
                : 'There are currently no service providers with pending registrations.'}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
        <div className="admin-table-scroll" role="region" aria-label="Pending registrations data table" tabIndex={0}>
          <table className="admin-data-table min-w-max">
            <thead>
              <tr>
                <th>
                      Status
                    </th>
                    <th 
                      onClick={() => handleSort('tradeName')}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('tradeName'); }}
                      tabIndex={0}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Trade Name</span>
                        {getSortIcon('tradeName')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('contactNumber')}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('contactNumber'); }}
                      tabIndex={0}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Contact</span>
                        {getSortIcon('contactNumber')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('email')}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('email'); }}
                      tabIndex={0}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Email</span>
                        {getSortIcon('email')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('servicesOffered')}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('servicesOffered'); }}
                      tabIndex={0}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Services Offered</span>
                        {getSortIcon('servicesOffered')}
                      </div>
                </th>
                <th 
                  onClick={() => handleSort('registeredOn')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('registeredOn'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Registered On</span>
                    {getSortIcon('registeredOn')}
                  </div>
                </th>
                <th>
                      Action
                </th>
              </tr>
            </thead>
            <tbody>
                  {filteredAndSortedPendingRegistrations.map((registration) => (
                    <tr key={registration.id}>
                    <td className="whitespace-nowrap">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          registration.verificationStatus === 'pending'
                            ? 'border-amber-200 bg-amber-50 text-amber-700'
                            : registration.verificationStatus === 'verified'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-red-200 bg-red-50 text-red-700'
                        }`}>
                          {registration.verificationStatus === 'pending' ? 'Pending' : 
                           registration.verificationStatus === 'verified' ? 'Verified' : 'Rejected'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                        <div className="font-medium text-slate-900">{registration.tradeName}</div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="text-slate-700">{registration.contactNumber}</div>
                    </td>
                    <td className="whitespace-nowrap">
                        <div className="text-slate-700">{registration.email}</div>
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-blue-50 px-2 text-sm font-semibold text-blue-700">{registration.servicesOffered.toString().padStart(2, '0')}</span>
                    </td>
                      <td className="whitespace-nowrap">
                        <div className="text-slate-700">
                          {formatDateTimeString(registration.registeredOn)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap">
                        <div className="flex gap-2">
                          <button 
                            type="button"
                            onClick={() => handleView(registration)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            aria-label={`View ${registration.tradeName}`}
                            title={`View ${registration.tradeName}`}
                          >
                            <Eye className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <button 
                            type="button"
                            onClick={() => handleApprove(registration)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                            aria-label={`Approve ${registration.tradeName}`}
                            title={`Approve ${registration.tradeName}`}
                          >
                            <Check className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <button 
                            type="button"
                            onClick={() => handleReject(registration)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 shadow-sm transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                            aria-label={`Reject ${registration.tradeName}`}
                            title={`Reject ${registration.tradeName}`}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                    </td>
                  </tr>
                  ))}
            </tbody>
          </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
              <div className="admin-table-footer">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-600">
                    Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} results
                  </div>
                  <nav className="inline-flex items-center -space-x-px self-start overflow-hidden rounded-lg border border-slate-200 shadow-sm" aria-label="Pagination">
            <button
                      type="button"
                      onClick={() => setCurrentPage(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
                      className="inline-flex h-9 w-9 items-center justify-center bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="sr-only">Previous page</span>
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
                    <span className="inline-flex h-9 min-w-9 items-center justify-center border-x border-slate-200 bg-slate-900 px-3 text-sm font-semibold text-white" aria-current="page">
                      {pagination.currentPage}
                    </span>
            <button
                      type="button"
                      onClick={() => setCurrentPage(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
                      className="inline-flex h-9 w-9 items-center justify-center bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="sr-only">Next page</span>
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </nav>
                </div>
              </div>
            )}
          </>
        )}
            </div>

      {/* Profile Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Service Provider Profile</h3>
                <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
            </div>
            
            <div className="p-6">
              {profileLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading profile details...</span>
                </div>
              ) : selectedProfile ? (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full Name</label>
                        <p className="text-sm text-gray-900">{selectedProfile.fullName}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Contact Number</label>
                        <p className="text-sm text-gray-900">{selectedProfile.contactNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{selectedProfile.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Trade Name</label>
                        <p className="text-sm text-gray-900">{selectedProfile.tradeName}</p>
                      </div>
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Business Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Service Provider Type</label>
                        <p className="text-sm text-gray-900">{selectedProfile.serviceProviderType?.name || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Categories</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedProfile.categories && selectedProfile.categories.length > 0 ? (
                            selectedProfile.categories.map((category, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {category.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No categories specified</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Specialization</label>
                        <p className="text-sm text-gray-900">{selectedProfile.specialization || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Business Address</label>
                        <p className="text-sm text-gray-900">{selectedProfile.businessAddress}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Status Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Profile Status</label>
                        <p className="text-sm text-gray-900 capitalize">{selectedProfile.profileStatus?.replace(/_/g, ' ')}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Payment Status</label>
                        <p className="text-sm text-gray-900 capitalize">{selectedProfile.paymentStatus?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Documents
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Profile Photo</label>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            selectedProfile.documents?.profilePhoto?.url ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedProfile.documents?.profilePhoto?.url ? 'Uploaded' : 'Not uploaded'}
                          </span>
                          {selectedProfile.documents?.profilePhoto?.url && (
                            <a 
                              href={selectedProfile.documents.profilePhoto.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Business ID Card</label>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            selectedProfile.documents?.businessIdCard?.url ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedProfile.documents?.businessIdCard?.url ? 'Uploaded' : 'Not uploaded'}
                        </span>
                          {selectedProfile.documents?.businessIdCard?.url && (
                            <a 
                              href={selectedProfile.documents.businessIdCard.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">State ID Card (Front)</label>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            selectedProfile.documents?.stateIdCardFront?.url ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedProfile.documents?.stateIdCardFront?.url ? 'Uploaded' : 'Not uploaded'}
                        </span>
                          {selectedProfile.documents?.stateIdCardFront?.url && (
                            <a
                              href={selectedProfile.documents.stateIdCardFront.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">State ID Card (Back)</label>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            selectedProfile.documents?.stateIdCardBack?.url ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedProfile.documents?.stateIdCardBack?.url ? 'Uploaded' : 'Not uploaded'}
                        </span>
                          {selectedProfile.documents?.stateIdCardBack?.url && (
                            <a
                              href={selectedProfile.documents.stateIdCardBack.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Registration Details */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Registration Details
                    </h4>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Registered On</label>
                      <p className="text-sm text-gray-900">{formatDateTimeString(selectedProfile.registeredOn)}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-red-600 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Profile</h3>
                  <p className="text-gray-600">Failed to load service provider profile details.</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-200">
                <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                >
                Close
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${
                  actionType === 'approve' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {actionType === 'approve' ? (
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {actionType === 'approve' ? 'Approve Service Provider' : 'Reject Service Provider'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to {actionType === 'approve' ? 'approve' : 'reject'} <strong>{selectedProfile.tradeName}</strong>?
                </p>
                <p className="text-xs text-gray-500">
                  Contact: {selectedProfile.contactNumber}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 px-6 py-4 bg-gray-50 rounded-b-lg">
              <button
                onClick={cancelAction}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 ${
                  actionType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700 focus-visible:ring-green-500'
                    : 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
                }`}
              >
                {actionLoading ? (
                  <div className="flex items-center">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {actionType === 'approve' ? 'Approving...' : 'Rejecting...'}
                  </div>
                ) : (
                  actionType === 'approve' ? 'Approve' : 'Reject'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Message */}
      {actionMessage && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`rounded-md p-4 shadow-lg ${
            actionMessage.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {actionMessage.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  actionMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {actionMessage.text}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
